import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { Group } from '@/client/groups';
import { getFiltersFromColumns } from '@/client/helpers';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import { GROUPS_GUIDE } from '@/common/constants/external-urls';
import { DialogContext } from '@/common/context';
import {
  DataTable,
  DataTableActions,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { useDeleteGroup, useGroups } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import GroupsBlankPageImg from '@/images/blank-pages/groups-blank-page.svg';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DialogType } from '@/system-settings/enums';
import { BlankPage } from '@/ui/blank-page/BlankPage';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import { isDemoRestrictedMode } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

export const GroupsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermission();
  const { skip, take, sort, apiFilters, onFilter, onSort, onPage } = useTable({
    tableName: TableNamesEnum.GROUPS_LIST,
  });

  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);

  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, currentUser);

  const { setDialogData } = useContext(DialogContext);

  const { isLoading: isInitialLoading, groups: initialGroups } = useGroups({
    accountId: account?.id,
    take: 1,
    skip: 0,
  });

  const { isLoading, groups, refetch, isFetching } = useGroups({
    accountId: account?.id,
    take,
    skip,
    filters: apiFilters,
    sort: sort && sort.length > 0 ? [sort.join(',')] : [],
    enabled: !!initialGroups?.count,
  });
  const deleteGroup = useDeleteGroup();

  const menuItems = (group: Group) => {
    const menu: MenuItem[] = [
      {
        label: t('users'),
        icon: 'pi pi-users',
        command: () =>
          navigate(RedirectPaths[RedirectPathsEnum.GROUPS_USERS](group.id), {
            state: { from: location.pathname + location.search },
          }),
      },
    ];

    if (
      can(Actions.UPDATE, Subjects.GROUPS) &&
      (!account?.isSystem || currentUser?.role.code === SystemRoles.DEVELOPER)
    ) {
      menu.push(
        ...[
          {
            items: [
              {
                label: t('generic.edit'),
                icon: 'pi pi-pencil',
                command: () => {
                  navigate(
                    RedirectPaths[RedirectPathsEnum.GROUPS_EDIT](group.id),
                    { state: { from: location.pathname + location.search } },
                  );
                },
              },
              ...(group?.isForPhishing
                ? []
                : [
                    {
                      label: t('generic.delete'),
                      icon: 'pi pi-times',
                      command: isDemoAccount
                        ? undefined
                        : () =>
                            setDialogData({
                              type: DialogType.CONFIRMATION,
                              show: true,
                              closeImmediatelyAfterAccept: true,
                              header: t('dialog.delete'),
                              message: t('dialog.delete.group'),
                              onAccept: async () =>
                                handleDeleteUserGroups(group.id),
                            }),
                    },
                  ]),
            ],
          },
        ],
      );
    }

    return menu;
  };

  const columns: DataTableColumnType[] = [
    {
      field: 'name',
      header: t('generic.name'),
      sortable: true,
      filterable: true,
      filters: { type: FilterTypeEnum.TEXT },
    },
    {
      field: 'activeUsersCount',
      header: t('groups.active.users'),
      sortable: true,
      filterable: false,
      render: (row: Group) => row?.activeUsersCount || 0,
    },
    {
      field: 'inactiveUsersCount',
      header: t('groups.inactive.users'),
      sortable: true,
      filterable: false,
      render: (row: Group) => row?.inactiveUsersCount || 0,
    },
    {
      field: 'updated',
      header: t('generic.updated'),
      sortable: true,
      filterable: false,
      render: (row: Group) =>
        row?.updated ? <FormatDate date={row?.updated} /> : t('generic.never'),
    },
    {
      field: 'autoAssign',
      header: t('group.autoAssign'),
      sortable: true,
      filterable: true,
      filters: {
        type: FilterTypeEnum.SELECT_YES_NO,
        placeholder: t('generic.select'),
      },
      render: (row: Group) =>
        row.autoAssign ? (
          <AppChip label={t('dialog.yes')} type="primary" size="s" />
        ) : (
          <AppChip label={t('dialog.no')} type="secondary" size="s" />
        ),
    },
    {
      field: 'isForPhishing',
      header: t('group.isForPhishing'),
      sortable: true,
      filterable: true,
      filters: {
        type: FilterTypeEnum.SELECT_YES_NO,
        placeholder: t('generic.select'),
      },
      render: (row: Group) =>
        row.isForPhishing ? (
          <AppChip label={t('dialog.yes')} type="primary" size="s" />
        ) : (
          <AppChip label={t('dialog.no')} type="secondary" size="s" />
        ),
    },

    {
      field: 'actions',
      header: t('generic.actions'),
      filterable: false,
      sortable: false,
      style: {
        width: '80px',
        textAlign: 'center' as const,
      },
      render: (row: Group) => {
        if (deletingGroupId === row.id) {
          return (
            <FlexContainer>
              <ProgressSpinner style={{ width: '24px', height: '24px' }} />
            </FlexContainer>
          );
        }
        return (
          <DataTableActions
            disabled={menuItems(row).length < 1}
            menuItems={menuItems(row)}
          />
        );
      },
    },
  ];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = columns
    .filter((column) => !['inactiveUsersCount'].includes(column.field))
    .map((column) => column.field);
  const alwaysVisibleColumns = ['name', 'actions'];
  //

  const handleDeleteUserGroups = async (groupId: string) => {
    if (isDemoAccount) return;
    setDeletingGroupId(groupId);
    try {
      await deleteGroup.delete({
        accountId: (account as Account)?.id,
        groupId,
      });
      await refetch();

      toast?.success(t('toast.success'), t('group.deleted'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setDeletingGroupId(null);
    }
  };

  const onRowClick = (e: DataTableRowClickEvent) => {
    if (isLoading) return;
    navigate(RedirectPaths[RedirectPathsEnum.GROUPS_EDIT](e.data.id), {
      state: { from: location.pathname + location.search },
    });
  };

  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        <DataTableFilters
          filters={getFiltersFromColumns(columns)}
          onFilter={onFilter}
        />
        {can(Actions.CREATE, Subjects.GROUPS) &&
          (!account?.isSystem ||
            currentUser?.role.code === SystemRoles.DEVELOPER) && (
            <AppButton
              label={t('button.createNew')}
              severity="secondary"
              onClick={
                isDemoAccount
                  ? undefined
                  : () => {
                      navigate(
                        RedirectPaths[RedirectPathsEnum.GROUPS_CREATE](),
                        {
                          state: { from: location.pathname + location.search },
                        },
                      );
                    }
              }
            />
          )}
      </FlexContainer>
      <DataTableColumnsMultiselect
        columns={columns}
        tableName={TableNamesEnum.GROUPS_LIST}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  const pathItems = [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id as string)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('groups'),
      url: RedirectPaths[RedirectPathsEnum.GROUPS](),
      template: AppBreadCrumbTemplate,
    },
  ];

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      style={{ minHeight: '100%' }}
    >
      <AppBreadCrumb model={pathItems} />
      <h1>{t('groups')}</h1>
      {!initialGroups?.count ? (
        <BlankPage
          isLoading={isInitialLoading}
          image={GroupsBlankPageImg}
          title={t('groups.blank.heading')}
          description={t('groups.blank.description')}
          mainCtaText={t('groups.blank.cta.create')}
          mainCtaOnClick={() =>
            navigate(RedirectPaths[RedirectPathsEnum.GROUPS_CREATE]())
          }
          secondaryCtaText={t('groups.blank.cta.readMore')}
          secondaryCtaLink={GROUPS_GUIDE}
          additionsText={[
            t('groups.blank.additions.1'),
            t('groups.blank.additions.2'),
            t('groups.blank.additions.3'),
          ]}
        />
      ) : (
        <DataTable
          data={groups?.result}
          count={groups?.count as number}
          isLoading={isLoading}
          isFetching={isFetching}
          toolbar={toolbar}
          columns={columns}
          visibleColumns={visibleColumns}
          onPage={onPage}
          rows={take}
          skip={skip}
          onSort={onSort}
          sort={sort}
          onRowClick={onRowClick}
        />
      )}
    </FlexContainer>
  );
};
